import React, { Component } from 'react';
import DashboardInformationComponent from '../components/dashboard/DashboardInformationComponent';
import { FormicaTimePickerDate } from '../components/custom/custom-time-picker/TimePickerDomain';
import { query, repository, subscribe } from '@trlogic/client-js';
import { InstanceContainer } from '../config/binding/InstanceBinding';
import Injectables from '../constant/Injectables';
import CollectionNames from '../constant/CollectionNames';
import { TransactionJsonModel } from '../domain/stream/transaction/TransactionDomains';
import { FormicaProps, withFormica } from '../helpers/withFormica';
import { RootState } from '../redux/store';
import { DashboardState } from '../redux/reducers/DashboardReducer';
import { changeDashboard, changeLoading, clearDashboard, setDashboardFilter, setDashboardResponse, setDashboardTransactions } from '../redux/actions/DashboardActions';
import { clearPage } from '../redux/actions/PageActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import CustomTabv2, { CustomTabItem } from '../components/custom/CustomTabv2';
import DashboardComponent from '../components/dashboard/DashboardComponent';
import DashboardAccessComponent from '../components/dashboard/DashboardAccessComponent';
import ComponentAuditLog from '../components/ComponentAuditLog';
import ComponentName from '../constant/ComponentName';
import DashboardService from '../service/DashboardService';
import ServiceContainer from '../config/ServiceContainer';
import { ServiceNames } from '../constant/ServiceNames';
import { ConfirmPopup } from 'primereact/confirmpopup';
import { AuthState } from '../redux/reducers/AuthReducer';
import { Dashboard, DashboardFilter } from '../domain/dashboard/DashboardDomains';
import EmptyComponent from '../components/custom/empty-components/EmptyComponent';
import DashboardEmailSettingsComponent from '../components/dashboard/DashboardEmailSettingsComponent';
import RoleHelper from '../RoleHelper';
import eq = query.eq;
import LanguageUtil from '../util/LanguageUtil';

class DashboardContainer extends Component<Props, State> {
  public state = new State();

  private transactionSubscription: subscribe.Subscription | undefined;
  private dashboardSubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;
  private repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);

  private dashboardService: DashboardService = ServiceContainer.getInstance().get(ServiceNames.DASHBOARD);

  public render() {
    const {
      dashboard: { dashboard, loading },
    } = this.props;

    if (!RoleHelper.canView('Dashboard')) return <div>No Permission</div>;
    return (
      <div className={'flex flex-col w-full h-full overflow-y-hidden'}>
        <ConfirmPopup />
        <DashboardInformationComponent resubscribeTracker={this.resubscribeTracker} onDateRangeApplied={this.onDateRangeApplied} />
        <DashboardEmailSettingsComponent />
        <div className={'flex w-full h-full'}>
          {!dashboard.selectedTransaction && !loading && (
            <div className={'flex w-full h-ful px-5 py-5'}>
              <EmptyComponent createDisabled={true} type={ComponentName.DASHBOARD} />
            </div>
          )}
          {dashboard.selectedTransaction && <CustomTabv2 isDashboard tab={this.getTabs()} />}
        </div>
      </div>
    );
  }

  public async componentDidMount() {
    await this.startTracker();
  }

  public async componentWillUnmount() {
    const { clearPage } = this.props;
    clearPage();
    await this.stopTracker();
  }

  private tracker = async () => {
    const {
      dashboard: { dashboard },
      auth: { user },
      changeDashboard,
      setDashboardFilter,
      setDashboardTransactions,
      changeLoading,
    } = this.props;
    if (this.transactionSubscription?.ready() && this.dashboardSubscription?.ready()) {
      const transactionQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.TRANSACTIONS).select('name', 'description', 'variables').build();
      const dashboardQuery: query.Query = query.QueryBuilder.select()
        .from(CollectionNames.DASHBOARDS)
        .where(eq('name', `${dashboard.selectedTransaction}_${user?.activeRiskType}_dashboard`))
        .build();
      const transactions: TransactionJsonModel[] | undefined = await this.repository.findMany(transactionQuery);
      const _dashboard: Dashboard | undefined = await this.repository.find(dashboardQuery);
      if (_dashboard) {
        delete _dashboard._id;
        const currentSelectedTransaction = dashboard.selectedTransaction;
        const savedFilter: DashboardFilter = { startDate: _dashboard.startDate, endDate: _dashboard.endDate };
        changeDashboard({ ..._dashboard, selectedTransaction: currentSelectedTransaction });
        setDashboardFilter(savedFilter);
        RoleHelper.setCurrentAccess(_dashboard);
        await this.getDashboardWithCharts(_dashboard.name, savedFilter);
      } else {
      }
      setDashboardTransactions(transactions);
      changeLoading(false);
    }
  };

  private startTracker = async () => {
    const {
      dashboard: { dashboard },
      auth: { user },
    } = this.props;
    const dashboardQuery = query.QueryBuilder.select()
      .from(CollectionNames.DASHBOARDS)
      .where(eq('name', `${dashboard.selectedTransaction}_${user?.activeRiskType}_dashboard`));
    this.dashboardSubscription = subscribe.Formica.subscribe(CollectionNames.DASHBOARDS, dashboardQuery.build());
    const transactionQuery = query.QueryBuilder.select().from(CollectionNames.TRANSACTIONS);
    this.transactionSubscription = subscribe.Formica.subscribe(CollectionNames.TRANSACTIONS, transactionQuery.build());
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.transactionSubscription, this.dashboardSubscription]);
  };

  private stopTracker = async () => {
    await this.transactionSubscription?.stop();
    await this.dashboardSubscription?.stop();
    this.computation?.stop();
  };

  private resubscribeTracker = async () => {
    if (this.computation == undefined) {
      await this.startTracker();
    } else {
      this.computation.onStop(this.startTracker);
      await this.stopTracker();
    }
  };

  private onDateRangeApplied = (startDate: FormicaTimePickerDate, endDate: FormicaTimePickerDate) => {
    const {dashboard: { dashboard }, setDashboardFilter} = this.props;
    const filter: DashboardFilter = { startDate, endDate };
    setDashboardFilter(filter);
    if (dashboard.name) {
      this.getDashboardWithCharts(dashboard.name, filter);
    }
  };

  private getDashboardWithCharts = async (dashboard: string, filter?: { startDate: FormicaTimePickerDate; endDate: FormicaTimePickerDate }) => {
    const { setDashboardResponse, changeLoading } = this.props;
    changeLoading(true);
    const response = await this.dashboardService.getDashboard(dashboard, filter);
    setDashboardResponse(response);
    changeLoading(false);
  };

  private getTabs = (): CustomTabItem[] => {
    const {
      dashboard: { dashboard },
    } = this.props;
    return [
      {
        key: 'Dashboard',
        label: LanguageUtil.get({ id: 'common.tabs.dashboard', defaultMessage: 'Dashboard' }),
        content: <DashboardComponent />,
      },
      {
        key: 'Access',
        label: LanguageUtil.get({ id: 'common.tabs.access', defaultMessage: 'Access' }),
        content: <DashboardAccessComponent />,
      },
      {
        key: 'Audit',
        label: LanguageUtil.get({ id: 'common.tabs.audit', defaultMessage: 'Audit' }),
        content: dashboard.selectedTransaction && (
          <ComponentAuditLog component={ComponentName.DASHBOARD} documentName={this.props.dashboard.dashboard?.name || undefined} canPreview />
        ),
      },
    ];
  };
}

interface Props extends ReduxProps, FormicaProps {}

const mapStateToProps = (state: RootState) => ({
  dashboard: state.dashboard as DashboardState,
  auth: state.auth as AuthState,
});
const mapDispatchToProps = {
  setDashboardTransactions,
  setDashboardResponse,
  changeDashboard,
  setDashboardFilter,
  changeLoading,
  clearPage,
  clearDashboard,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

class State {}

export default withFormica(connector(DashboardContainer));
